import {Injectable} from "@angular/core";
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from "@angular/common/http";
import {from, Observable, of, throwError, timer} from 'rxjs';
import {catchError, map, retry, switchMap} from 'rxjs/operators';
import {environment} from "../../environments/environment";
import {InterruptError} from "../errors/interrupt-error";
import {AlertService} from '../services/sp-alert/alert.service';
import {LoggerService} from '../services/sp-logger/logger.service';
import { SessionProviderService } from '../services/sp-authentication/session-provider.service';
import { Router } from '@angular/router';
import { HttpMockHandlerService } from '../services/http-mock-handler.service';

@Injectable()
export class ApiInterceptor implements HttpInterceptor {
  constructor(
    private alert: AlertService,
    private logger: LoggerService,
    private sessionProvider: SessionProviderService,
    private router: Router,
    private mockHandler: HttpMockHandlerService
  ) { }

  /**
   * API Interceptor for errors
   * @param req
   * @param next
   */
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return this.handleRequest(req, next);
  }

  handleRequest(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!req.headers.has('Authorization')) {
      // Check if session exists before trying to get token
      if (this.sessionProvider.activeSession?.authTokenProvider) {
        // Use switchMap to handle the async token retrieval
        return from(this.sessionProvider.activeSession.authTokenProvider.getAccessToken()).pipe(
          switchMap(token => {
            if (token) {
              req = req.clone({
                headers: req.headers
                    .set("Authorization", "Bearer " + token)
                    .set("Accept", "application/json")
              });
            }

            return this.processRequest(req, next);
          })
        );
      } else {
        // No session available, proceed without token
        return this.processRequest(req, next);
      }
    } else {
      return this.processRequest(req, next);
    }
  }

  private processRequest(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Check if we should mock this request (only when debug features enabled)
    if (environment.config.enableLocalDevelopmentFeatures && this.mockHandler.shouldMock(req)) {
      const mockResponse$ = this.mockHandler.getMockResponse(req);
      if (mockResponse$) {
        return mockResponse$.pipe(
          map(body => new HttpResponse({ status: 200, body }))
        );
      }
    }

    return next.handle(req).pipe(
      map ((event: HttpEvent<any>) => {
        let data = event;
        if (event instanceof HttpResponse) {
          /* Handle successful responses here */

          if(event.body && event.body.message && event.body.message.type) {
            const type = event.body.message.type;
            const content = event.body.message.content

            switch (type) {
              case "info": this.alert.notify("Info", content, "info"); break; // Info notification
              case "success": this.alert.notify("Success", content, "success"); break; // Success notification
              case "warning": this.alert.notify("Warning", content, "warning"); break; // Warning notification
              case "error": this.alert.notify("Error", content, "error"); break; // Error notification
              default: {
                if(!environment.config.production && !environment.config.demo) {
                  this.alert.notify("Unknown message", "Unknown message type '" + event.body.message.type + "' from Core API", "warning");
                }
              }
            }

            if(event.body.message.interrupt) {
              this.logger.logError(event.body);
              throw new InterruptError(event.body.message.content);
            }
          }

          /* -------------------------------------------------- */
        }

        return data;
      }),
      retry({
        count: 2,
        delay: (error, retryCount) => {
          if (error instanceof HttpErrorResponse && error.status === 0 && ['GET', 'HEAD', 'OPTIONS'].includes(req.method)) {
            return timer(1000 * retryCount);
          }
          throw error;
        }
      }),
      catchError ((response: Error) => {
        if (response instanceof HttpErrorResponse) {
          /* Handle errors here */
          if (environment.config.apiErrorCheck && (response.url.includes(environment.config.api.baseUrl) || response.url.includes(environment.config.api.statsUrl))) {
            let status = response.status;
            if (status === 0) {
              const chunkFailedMessage = /Loading chunk [\d]+ failed/;

              if (chunkFailedMessage.test(response.message)) {
                window.location.reload();
              }
            }
            
            if (status === 401) {
              if (this.sessionProvider.activeSession) {
                this.sessionProvider.logout();
              }
              return throwError(() => response);
            }

            if (status >= 500) {
              if (status === 503) {
                if (response.error.message === "Service Unavailable") {
                  this.router.navigate(['/page-maintenance']);
                  return of(null);
                } else {
                  this.logger.logError(response.error);
                  this.alert.notify("Error", response.error.message, "error");
                }
              }
              if (status === 504) {
                this.logger.logWarning("504 Timeout received");
              }
            }
          }
          /* -------------------------------------------------- */

          return throwError(() => response);
        }
        return throwError(() => response);
      })
    );
  }
}
